import React, { useCallback, useEffect } from 'react';

import { CloseOutlined, FileDownloadDoneOutlined } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams } from 'react-router';

import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerButton } from '@/features/Dashboard/drawer/DrawerButton';
import { DrawerHeader } from '@/features/Dashboard/drawer/DrawerHeader';
import { useBuildDashboardStructure } from '@/features/Dashboard/hooks/useBuildDashboardStructure';
import { useCurrentDashboardConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import { MarketApp } from '@/features/Dashboard/types';
import { useUpdateDashboard } from '@/hooks/api/dashboard';

const AddAppHeader = ({
  createWidget,
  isValid
}: {
  createWidget: () => MarketApp | undefined;
  isValid: boolean;
}) => {
  const { initialDashboardName } = useEditDashboardDraft();
  const {
    closeDrawer,
    handleAttemptBack,
    isEditMode,
    editingTarget,
    setEditingTarget,
    handleAttemptClose
  } = useDrawerSteps();
  const { addWidget, updateTab, setTiles } = useEditDashboardDraft();
  const allDashboardTabs = useCurrentDashboardConfig();

  const params = useParams<{ tabName?: string }>();
  const currentTabName = params.tabName || Object.keys(allDashboardTabs || {})[0] || 'Main';

  const buildDashboardStructure = useBuildDashboardStructure();

  const { mutate, isSuccess, isPending } = useUpdateDashboard(
    initialDashboardName,
    'New app added!',
    currentTabName
  );

  useEffect(() => {
    if (isSuccess) {
      closeDrawer();
    }
  }, [isSuccess, closeDrawer]);

  const handleSave = useCallback(
    (widget: MarketApp | undefined) => {
      if (!widget) return;
      // In edit mode of the Dashboard, update existing if editing target is set; otherwise add new to draft
      if (isEditMode) {
        if ('type' in widget && widget.type === 'tiles') {
          setTiles(widget.tiles);
          setEditingTarget(null);
          closeDrawer();
          return;
        }
        if (editingTarget?.appId && editingTarget?.tabId && updateTab && 'tabs' in widget) {
          const updatedTab = (widget as any).tabs?.[0];
          if (updatedTab) {
            updateTab(editingTarget.appId, editingTarget.tabId, updatedTab);
            setEditingTarget(null);
            closeDrawer();
            return;
          }
        }

        addWidget(widget);
        closeDrawer();
        return;
      }

      const structurePayload = buildDashboardStructure(widget);
      mutate(structurePayload);
    },
    [
      addWidget,
      buildDashboardStructure,
      closeDrawer,
      editingTarget,
      isEditMode,
      mutate,
      setEditingTarget,
      setTiles,
      updateTab
    ]
  );

  return (
    <DrawerHeader
      before={
        <DrawerButton
          disabled={isPending}
          icon={<ArrowBackIcon />}
          title='Go back'
          variant='secondary'
          onClick={handleAttemptBack}
        />
      }
      buttons={
        <>
          <DrawerButton
            disabled={isPending || !isValid}
            icon={<FileDownloadDoneOutlined />}
            title='Save'
            onClick={() => {
              handleSave(createWidget());
            }}
          />
          <DrawerButton
            disabled={isPending}
            icon={<CloseOutlined />}
            title='Discard'
            variant='secondary'
            onClick={() => {
              if (!isPending) {
                handleAttemptClose();
              }
            }}
          />
        </>
      }
      title={isEditMode ? 'Edit app' : 'Add new app'}
    />
  );
};

export default AddAppHeader;
